import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ONCHAIN_ESTIMATES,
  formatOnchainEstimate,
} from "../../constants/onchainEstimates";
import { logger } from "../../utils/logger";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import {
  WalletConnectTimeoutError,
  isWalletConnectAborted,
  isWalletConnectSuperseded,
} from "../../utils/wallet/providers/types";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import { useChangeText } from "../../hooks/useChangeText";
import { WalletEnum, isSdkWalletEnum } from "../../utils/wallet/types";
import { IConnectWalletType } from "./ConnectWallet";
import { EIP6963ProviderDetail } from "../../hooks/wallet/useEIP6963";
import { IConnectWalletModalProps, ConnectWalletPageEnum } from "./types";
import { useModalSetup } from "./useModalSetup";

type Ctx = IConnectWalletModalProps & ReturnType<typeof useModalSetup>;

export function useModalPaging(ctx: Ctx) {
  const {
    inviteStatus,
    inviteCode,
    availablePages,
    connectWalletInFlightRef,
    account,
    setSelectedWallet,
    setSelectedEIP6963Provider,
    activate,
    getImportedAccountAddress,
    reconnectImportedAccount,
    onHide,
    addToast,
    t,
  } = ctx;

  const [pageIndex, setPageIndex] = useState(0);
  const inviteRequired = Boolean(
    inviteStatus?.invite_required && !inviteStatus.allowed
  );

  useEffect(() => {
    const normalized = inviteCode.trim();
    if (normalized) {
      window.localStorage.setItem(LocalStorageKeyEnum.ACCESS_CODE, normalized);
    }
  }, [inviteCode]);

  // Update page index based on pageEnum
  const setCurrentPageIndex = useCallback(
    (pageEnum: ConnectWalletPageEnum) => {
      for (let i = 0; i < availablePages.length; i += 1) {
        if (availablePages[i] === pageEnum) {
          setPageIndex(i);
        }
      }
    },
    [availablePages]
  );

  // Update page index based on index
  const updatePageIndex = useCallback(
    (idx: number) => {
      let newIndex = idx;
      if (idx >= availablePages.length) {
        newIndex = availablePages.length - 1;
      } else if (idx < 0) {
        newIndex = 0;
      }
      setPageIndex(newIndex);
    },
    [availablePages.length]
  );

  // Select wallet and immediately trigger connection (skip Continue button)
  const connectAttemptRef = useRef(0);
  const setWalletAndConnect = useCallback(async (wallet: IConnectWalletType, eip6963Provider?: EIP6963ProviderDetail) => {
    const isSdk = isSdkWalletEnum(wallet);
    // SDK re-clicks must be allowed while a connect is in flight: the header
    // button otherwise no-ops until the 1.5s cancel grace, and Para stays closed.
    if (connectWalletInFlightRef.current && !isSdk) {
      return;
    }

    const attempt = ++connectAttemptRef.current;
    connectWalletInFlightRef.current = true;
    setSelectedWallet(wallet);
    setSelectedEIP6963Provider(eip6963Provider);

    try {
      if (!account) {
        setIsConnectingToWallet(wallet);
        if (wallet !== "manualImport") {
          setLoading(true);
          try {
            // The rdns goes to activate(), which owns the selection record --
            // both clearing it on entry and writing it back on success. It used
            // to be written here, which left the other activate() call site
            // (useModalActions' CTA) silently without one.
            await activate(
              wallet,
              wallet === WalletEnum.EIP6963 ? eip6963Provider?.provider : undefined,
              wallet === WalletEnum.EIP6963 ? eip6963Provider?.info?.rdns : undefined
            );
          } finally {
            setLoading(false);
          }
        } else if (getImportedAccountAddress()) {
          reconnectImportedAccount();
        } else {
          setShowImportManuallyModal(true);
          setIsConnectingToWallet(undefined);
          return;
        }
        setIsConnectingToWallet(undefined);
      }
      if (attempt !== connectAttemptRef.current) return;
      updatePageIndex(pageIndex + 1);
    } catch (err) {
      if (attempt !== connectAttemptRef.current) return;
      // A cancel is the expected exit and stays quiet. A TIMEOUT is not: it means
      // a wallet SDK failed to answer, and the user is owed an explanation rather
      // than a silently dead button.
      //
      // The comment that used to live here said "SDK modals reject on dismiss by
      // design". That assumption was wrong twice — Para (0b598f9) and Privy — and
      // both times the recovery below never ran, because a promise that never
      // settles reaches no `catch` at all. Settlement is now guaranteed by
      // `usePendingConnect` plus the net in `useWallet.activate`, so this really
      // is reached.
      if (err instanceof WalletConnectTimeoutError) {
        logger.error("[connect] wallet connect timed out", err);
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: t("wallet_connect_timed_out_title"),
            subheader: t("wallet_connect_timed_out_desc"),
            status: ToastStatusEnum.ERROR,
          },
          8000
        );
      } else if (isWalletConnectAborted(err)) {
        logger.debug("[connect] wallet connect aborted:", err);
      } else {
        // Genuinely unexpected: previously swallowed into console.debug.
        logger.warn("[connect] wallet connect failed:", err);
      }
      // On SDK chains (Privy/Para/DogeOS) the SDK's native modal IS the wallet
      // picker, so our intermediate "Connect <SDK>" step is redundant. Cancelling
      // the SDK modal should close the whole connect flow rather than leave the
      // wrapper stuck on that step. (For web3-react wallets we stay put so the
      // user can pick a different wallet.)
      //
      // A superseded connect must NOT close the flow — a newer click is opening
      // the SDK modal again.
      if (isSdk && !isWalletConnectSuperseded(err)) {
        onHide?.();
      }
    } finally {
      if (attempt === connectAttemptRef.current) {
        setIsConnectingToWallet(undefined);
        connectWalletInFlightRef.current = false;
      }
    }
  }, [account, activate, addToast, getImportedAccountAddress, onHide, pageIndex, reconnectImportedAccount, t, updatePageIndex]);

  const pendingDepositTitles = useMemo(
    () => [
      t("initiating_deposit"),
      t("eta", {
        time: formatOnchainEstimate(ONCHAIN_ESTIMATES.deposit),
      }),
    ],
    [t]
  );

  const [isLoading, setLoading] = useState(false);
  const [loadingAllowance, setLoadingAllowance] = useState(false);
  const [isConnectingToWallet, setIsConnectingToWallet] =
    useState<IConnectWalletType>();
  const [showImportManuallyModal, setShowImportManuallyModal] = useState(false);

  const [pendingDepositTxURL, setPendingDepositTxURL] = useState<
    string | undefined
  >();
  const pendingDepositTitle = useChangeText(
    pendingDepositTitles,
    5000,
    !!pendingDepositTxURL
  );

  const [showWalletInfoModal, setShowWalletInfoModal] = useState(false);
  const [isRememberMeChecked, setRememberMe] = useState(true);

  return {
    pageIndex,
    setPageIndex,
    inviteRequired,
    setCurrentPageIndex,
    updatePageIndex,
    setWalletAndConnect,
    pendingDepositTitles,
    isLoading,
    setLoading,
    loadingAllowance,
    setLoadingAllowance,
    isConnectingToWallet,
    setIsConnectingToWallet,
    showImportManuallyModal,
    setShowImportManuallyModal,
    pendingDepositTxURL,
    setPendingDepositTxURL,
    pendingDepositTitle,
    showWalletInfoModal,
    setShowWalletInfoModal,
    isRememberMeChecked,
    setRememberMe,
  };
}
